import { useRef, useState } from "react";
import { API } from "@/lib/api";

/**
 * FocalPointPicker — click anywhere on the image to set the "focal point".
 * Stores normalized coordinates (0..1) so they survive any future crop/resize.
 *
 * Props:
 *  - photo: { storage_path, focal_x?, focal_y?, title? }
 *  - onChange: ({ focal_x, focal_y }) => void
 *  - aspectPreviews: array of strings (e.g. ["aspect-[16/9]", "aspect-square"]) for live preview tiles
 */
export default function FocalPointPicker({ photo, onChange, aspectPreviews = ["aspect-[16/9]", "aspect-square", "aspect-[4/5]"] }) {
  const wrapperRef = useRef(null);
  const [fx, setFx] = useState(typeof photo?.focal_x === "number" ? photo.focal_x : 0.5);
  const [fy, setFy] = useState(typeof photo?.focal_y === "number" ? photo.focal_y : 0.5);

  const handlePick = (e) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setFx(x);
    setFy(y);
    onChange?.({ focal_x: x, focal_y: y });
  };

  if (!photo?.storage_path) return null;
  const pos = `${(fx * 100).toFixed(2)}% ${(fy * 100).toFixed(2)}%`;

  return (
    <div className="space-y-4" data-testid="focal-picker">
      <div>
        <p className="text-[11px] tracking-widest uppercase text-[#8B8680] mb-2">Point d'intérêt — cliquez sur l'image</p>
        <div
          ref={wrapperRef}
          onClick={handlePick}
          className="relative cursor-crosshair rounded-2xl overflow-hidden bg-[#FAF3EA] select-none"
          style={{ aspectRatio: photo.width && photo.height ? `${photo.width}/${photo.height}` : "4/3" }}
        >
          <img
            src={`${API}/files/${photo.storage_path}`}
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            draggable={false}
          />
          {/* Crosshair marker */}
          <div
            className="absolute w-7 h-7 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,0.6)] pointer-events-none"
            style={{
              left: `calc(${(fx * 100).toFixed(2)}% - 14px)`,
              top: `calc(${(fy * 100).toFixed(2)}% - 14px)`,
              background: "rgba(198, 142, 89, 0.4)",
            }}
            data-testid="focal-marker"
          />
        </div>
      </div>

      {/* Live previews */}
      <div>
        <p className="text-[11px] tracking-widest uppercase text-[#8B8680] mb-2">Aperçu selon les ratios</p>
        <div className="grid grid-cols-3 gap-2">
          {aspectPreviews.map((aspect) => (
            <div key={aspect} className={`rounded-xl overflow-hidden bg-[#FAF3EA] ${aspect}`}>
              <img
                src={`${API}/files/${photo.storage_path}`}
                alt=""
                className="w-full h-full object-cover"
                style={{ objectPosition: pos }}
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
